import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { DocketService } from '../../shared/index';
import { ColDef, GridOptions, SelectionChangedEvent } from 'ag-grid-community';
import { dateComparator } from '../../shared/helpers/DateComparator';
import { Docket } from '../../models/index';
import { CustomerNamePipe } from '../../shared/pipes/customer-name.pipe';
import { CustomerNameCellComponent } from '../../shared/ag-grid-cells/customer-name-cell.component';
import { PreviewDocketCellComponent } from '../../shared/ag-grid-cells/preview-docket-cell.component';
import { DocketNumberAccountReferenceCellComponent } from '../../shared/ag-grid-cells/docket-number-account-reference-cell.component';
import { ReportCriteriaItemModel } from '../shared/models/report-criteria-item.model';
import { DateFormatPipe } from 'ngx-moment';
import { ConfigService } from '../../shared/services/config.service';
import { BalanceReportDataSource } from '../../shared/helpers/BalanceReportDataSource';
import { DecimalPipe, DOCUMENT, NgStyle } from '@angular/common';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { AgGridAngular, AgGridModule } from 'ag-grid-angular';
import { OrderByComponent } from '../shared/order-by/order-by.component';
import { MatButton } from '@angular/material/button';
import { FlexModule } from '@angular/flex-layout';
import { ReportCriteriaComponent } from '../shared/report-criteria/report-criteria.component';

@Component({
  selector: 'app-outstanding-balance-report',
  templateUrl: './outstanding-balance-report.component.html',
  styleUrls: ['./outstanding-balance-report.component.css'],
  providers: [DocketService, DateFormatPipe, CustomerNamePipe, DecimalPipe],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    ReportCriteriaComponent,
    FlexModule,
    MatButton,
    OrderByComponent,
    AgGridModule,
    NgStyle,
    ExtendedModule
  ]
})
export class OutstandingBalanceReportComponent implements OnInit {
  @ViewChild('formContainer', { static: true }) formContainer;

  types = [
    { value: 'Contracts', id: 'contracts' },
    { value: 'Invoices', id: 'invoices' },
    { value: 'All', id: '*' }
  ];
  columns: ColDef[] = [];
  @ViewChild('agGrid') grid!: AgGridAngular;
  availableCriteria: ReportCriteriaItemModel[] = [
    {
      name: 'Docket Type',
      type: 'dropDown',
      reference: 'docketType',
      used: true,
      defaultValue: '*',
      options: this.types
    }
  ];

  orderByCols = [
    { name: 'Customer', value: 'full_name' },
    { name: 'Invoice No.', value: 'account_reference' },
    { name: 'Balance', value: 'balance' }
  ];

  gridOptions: GridOptions = {
    rowModelType: 'infinite',
    paginationPageSize: 500,
    cacheBlockSize: 500,
    headerHeight: 45,
    cacheOverflowSize: 2,
    maxConcurrentDatasourceRequests: 2,
    infiniteInitialRowCount: 1,
    rowMultiSelectWithClick: false
  };

  selectedRows: Docket[] = [];

  orderBy = {
    order: 'id',
    direction: 'asc'
  };

  reportForm = new UntypedFormGroup({});

  reportFetched = false;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private docketService: DocketService,
    private datePipe: DateFormatPipe,
    private decimalPipe: DecimalPipe
  ) {}

  ngOnInit() {
    this.columns = [
      {
        headerName: '',
        checkboxSelection: true
      },
      {
        headerName: 'Customer',
        cellRenderer: CustomerNameCellComponent,
        field: 'full_name'
      },
      {
        headerName: 'Contract',
        cellRenderer: DocketNumberAccountReferenceCellComponent,
        valueGetter: (params) => {
          return params.data;
        }
      },
      {
        headerName: 'Created Date',
        field: 'created',
        valueGetter: (params) => {
          if (params.data) {
            return this.datePipe.transform(
              params.data['created'],
              ConfigService.dates.shortDateTimeFormat
            );
          }
        },
        comparator: dateComparator
      },
      {
        headerName: 'Invoice Date',
        field: 'closed',
        valueGetter: (params) => {
          if (params.data) {
            return this.datePipe.transform(
              params.data['closed'],
              ConfigService.dates.shortDateTimeFormat
            );
          }
        },
        comparator: dateComparator
      },
      {
        headerName: 'Balance',
        field: 'balance',
        valueGetter: (params) => {
          if (params.data) {
            return this.decimalPipe.transform(params.data['balance'], '1.2-2');
          }
        },
        comparator: dateComparator
      },
      {
        headerName: 'Preview',
        cellRenderer: PreviewDocketCellComponent
      }
    ];
  }

  getDockets(isExport = false) {
    this.reportFetched = true;
    let params = this.availableCriteria
      .filter((criteria) => criteria.used)
      .reduce((val, used) => {
        val[used.reference] = {
          type: used.type,
          value: used.formControl.value
        };
        return val;
      }, {});
    if (isExport) {
      this.exportReporToXls(params);
      return;
    }
    const dataSource = new BalanceReportDataSource(
      this.docketService,
      params,
      this.orderBy
    );
    this.grid.api.setGridOption('datasource', dataSource);
    this.reportForm.markAsPristine();
  }

  exportReporToXls(params) {
    this.docketService
      .generateBalanceReportXls(
        {
          excludeDeleted: 1,
          orderBy: this.orderBy.order,
          orderDir: this.orderBy.direction,
          export: true
        },
        params
      )
      .subscribe((data) => {
        const blob = new Blob([data] as any, {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const url = this.document.defaultView.URL.createObjectURL(blob);
        const a = this.document.createElement('a');
        this.document.body.appendChild(a);
        a.setAttribute('style', 'display: none');
        a.href = url;
        a.download = 'Docket-Balance.xlsx';
        a.click();
        this.document.defaultView.URL.revokeObjectURL(url);
        a.remove(); // remove the element
      });
  }

  onSelectionChanged(event: SelectionChangedEvent) {
    this.selectedRows = event.api.getSelectedRows();
  }

  setOrderBy(event) {
    this.orderBy = event;
    this.reportForm.markAsDirty();
  }
}
